import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { resolveSpeakerSide, type SpeakerRoleMap } from '@/lib/speaker-roles';
import {
  capitalizeSpeakerName,
  speakerLabel,
  speakerNamesFor,
} from '@/lib/transcript-speaker';

interface DiarizationSegment {
  speaker: string;
  start: number;
  end: number;
}

export interface SpeakerTalkRatioProps {
  segments: DiarizationSegment[];
  /**
   * Role per diarization label from the analysis. Authoritative: the raw ASR
   * labels (`speaker_0`/`speaker_1`) that the protected source transcript
   * carries say nothing about who spoke.
   */
  speakerRoles?: SpeakerRoleMap;
  agentId?: string | null;
  agentName?: string;
  participantName?: string | null;
  className?: string;
}

/**
 * Bucket key for a label: a side when one can be established, the raw label
 * otherwise (an unplaceable speaker keeps a bucket of its own rather than
 * being folded into a side).
 *
 * The ladder is `@/lib/speaker-roles`, shared with SpeechSection's talk-ratio
 * bar. It used to be a private copy that ignored `agentName` entirely — the
 * name only picked a colour here, and by exact match — so a segment labelled
 * with the agent's name was `agent` on one bar of the screen and its own
 * nameless bucket on the other.
 */
function normalizeSpeaker(
  speaker: string,
  speakerRoles?: SpeakerRoleMap,
  agentName?: string
): string {
  return resolveSpeakerSide(speaker, speakerRoles, agentName) ?? speaker.toLowerCase();
}

function calculateTalkRatio(
  segments: DiarizationSegment[],
  speakerRoles?: Record<string, 'agent' | 'client' | 'unknown'>,
  agentName?: string
): { speaker: string; percent: number }[] {
  const totals: Record<string, number> = {};
  for (const seg of segments) {
    const key = normalizeSpeaker(seg.speaker, speakerRoles, agentName);
    totals[key] = (totals[key] ?? 0) + (seg.end - seg.start);
  }
  const total = Object.values(totals).reduce((a, b) => a + b, 0);
  if (total === 0) return [];

  // Largest remainder method: guarantees percentages sum to exactly 100
  const entries = Object.entries(totals).map(([speaker, ms]) => ({
    speaker,
    exact: (ms / total) * 100,
    percent: Math.floor((ms / total) * 100),
  }));
  const remainder = 100 - entries.reduce((s, e) => s + e.percent, 0);
  entries
    .slice()
    .sort((a, b) => b.exact - b.percent - (a.exact - a.percent))
    .slice(0, remainder)
    .forEach((e) => {
      e.percent += 1;
    });

  return entries.map(({ speaker, percent }) => ({ speaker, percent }));
}

// Mirrors SpeechSection's talk-ratio bar: agent=primary, client=accent.
const SPEAKER_COLORS: Record<string, string> = {
  agent: 'var(--color-primary)',
  client: 'var(--color-accent)',
};

// Unmapped speakers (real names from speakerMap, raw SPEAKER_N) get distinct
// chart-palette colors by order of appearance. chart-1 (teal) collides with
// the agent color and chart-4 (coral) reads as danger — both excluded.
const FALLBACK_COLORS = [
  'var(--color-chart-2)', // gold
  'var(--color-chart-5)', // periwinkle
  'var(--color-chart-3)', // mint
  'var(--color-chart-6)', // lavender
  'var(--color-chart-7)', // amber
  'var(--color-chart-8)', // sky
];

/**
 * Assigns colors by role instead of appearance order: a speaker whose
 * (normalized) label matches the record's agent name gets primary, mirroring
 * SpeechSection's agent/client split. Without a match (no agentName, or none
 * of the labels match it), every speaker falls back to the original
 * appearance-order palette — unchanged behavior.
 */
function assignSpeakerColors(
  speakers: string[],
  agentName?: string
): Map<string, string> {
  const colors = new Map<string, string>();
  const remaining: string[] = [];

  for (const speaker of speakers) {
    const known = SPEAKER_COLORS[speaker];
    if (known) {
      colors.set(speaker, known);
    } else {
      remaining.push(speaker);
    }
  }

  // The agent may already be a resolved side — `normalizeSpeaker` folds a
  // label carrying the record's agent name into `agent`, so it is coloured
  // above rather than waiting here to be matched by name. Either way the
  // agent IS identified, and the first remaining speaker is the other side.
  const normalizedAgent = agentName?.trim().toLowerCase();
  let agentMatched = speakers.includes('agent');
  if (!agentMatched && normalizedAgent) {
    const idx = remaining.findIndex((s) => s.trim().toLowerCase() === normalizedAgent);
    if (idx !== -1) {
      colors.set(remaining[idx]!, 'var(--color-primary)');
      remaining.splice(idx, 1);
      agentMatched = true;
    }
  }

  let fallbackIdx = 0;
  remaining.forEach((speaker, i) => {
    // First non-agent speaker plays the "other side" once the agent is
    // identified by name; any further speaker (3rd+) uses the fallback queue.
    colors.set(
      speaker,
      agentMatched && i === 0
        ? 'var(--color-accent)'
        : FALLBACK_COLORS[fallbackIdx++ % FALLBACK_COLORS.length]!
    );
  });

  return colors;
}

export function SpeakerTalkRatio({
  segments,
  speakerRoles,
  agentId,
  agentName,
  participantName,
  className,
}: SpeakerTalkRatioProps) {
  const { t } = useTranslation('records');

  // The legend names a bucket the way the transcript names its lines (#1105):
  // one source, `speakerLabel`. The bucket is still the SIDE (colours and
  // percentages need it), but a side found by the agent's name used to be
  // captioned «Оператор» while the lines beside it said «Дмитро Сіка». When a
  // bucket folds labels that the transcript names differently, no one name is
  // true for it, and the role word is.
  //
  // Memoized, and per distinct label rather than per segment: the parent
  // re-renders on every audio time update, and an hour-long call carries
  // thousands of segments over a handful of speakers.
  const bucketLabels = useMemo(() => {
    const speakers = [...new Set(segments.map((segment) => segment.speaker))];
    const names = speakerNamesFor(speakers, {
      speakerRoles,
      agentId,
      agentName,
      participantName,
    });
    const labels = new Map<string, Set<string>>();
    for (const speaker of speakers) {
      const key = normalizeSpeaker(speaker, speakerRoles, agentName);
      const bucket = labels.get(key) ?? new Set<string>();
      bucket.add(speakerLabel(speaker, names, t));
      labels.set(key, bucket);
    }
    return labels;
  }, [segments, speakerRoles, agentId, agentName, participantName, t]);

  const getSpeakerLabel = (speaker: string): string => {
    const labels = bucketLabels.get(speaker);
    if (labels?.size === 1) return [...labels][0]!;
    if (speaker === 'agent') return t('records.speakerAgent', 'Agent');
    if (speaker === 'client') return t('records.speakerClient', 'Client');
    return capitalizeSpeakerName(speaker);
  };

  const ratios = useMemo(
    () => calculateTalkRatio(segments, speakerRoles, agentName),
    [segments, speakerRoles, agentName]
  );

  if (ratios.length === 0) return null;

  const speakerColors = assignSpeakerColors(
    ratios.map((r) => r.speaker),
    agentName
  );
  const colored = ratios.map((r) => ({
    ...r,
    color: speakerColors.get(r.speaker) ?? 'var(--color-fg-muted)',
  }));

  return (
    <div data-testid="speaker-talk-ratio" className={cn('space-y-3', className)}>
      {/* Composite bar */}
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-bg-muted">
        {colored.map((r) => (
          <div
            key={r.speaker}
            className="h-full transition-all duration-300"
            style={{ width: `${r.percent}%`, backgroundColor: r.color }}
          />
        ))}
      </div>

      {/* Labels */}
      <div className="flex items-center gap-4 flex-wrap">
        {colored.map((r) => (
          <div key={r.speaker} className="flex items-center gap-2 text-sm">
            <span
              className="inline-block h-3 w-3 rounded-full"
              style={{ backgroundColor: r.color }}
            />
            <span className="text-fg-muted">{getSpeakerLabel(r.speaker)}</span>
            <span className="font-medium text-fg">{r.percent}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}
